import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { OfferModalAction } from '../../../../../shared/directives/offer-modal-action/offer-modal-action';
import {
  BuyErrors,
  LaunchDarklyService,
  DateUtilsService,
  multiLanguageAlphaNumericRegexHelper
} from 'remarketing-angular-library';
import {
  AcceptOffer,
  Cancel,
  CounterOffer,
  DeclineOffer,
  AnalyticsService,
  CurrencyFormatService
} from 'smartauction-frontend-micro-library';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { get } from 'lodash';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FeeToolTipService } from '../../../../../shared/services/feetooltip/feetooltip.service';
import {
  CharacterCountComponent,
  CharacterCountDirective,
  CurrencyInputComponent,
  DatalistItemComponent,
  InputConfig,
  TooltipComponent
} from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';
import { ActionSubmittingComponent } from 'src/shared/ui/comment/modal-actions/action-submitting/action-submitting.component';
import { ActionSubmittedComponent } from 'src/shared/ui/comment/modal-actions/action-submitted/action-submitted.component';
import { OfferModalsHeaderComponent } from '../../../../../shared/ui/comment/modal-actions/offer-header/offer-modals-header.component';
import { VehicleChangedWarningComponent } from '../../vehicle-changed-warning/vehicle-changed-warning.component';
import { OfferHistoryComponent } from '../../offer-history/offer-history.component';
import { ModalCloseComponent } from '../../../../../shared/ui/comment/modal-actions/modal-close/modal-close.component';
import { DripFeeSource, DripPricingService } from '../../../../../shared/services/drip-pricing/public-api';
import {
  ModalPriceBreakdownComponent,
  modalTotalDueOf,
  showsTransportationFee
} from '../../../../../shared/components/modal-price-breakdown/modal-price-breakdown.component';
import {
  ModalPriceBreakdownPattern,
  ModalPriceBreakdownRow,
  ModalPriceBreakdownTranslations
} from '../../../../../shared/components/modal-price-breakdown/modal-price-breakdown.types';

/**
 * The three actions the buyer can take on a seller offer, as tabs (M4.9).
 *
 * Declared here rather than shared with counter-offer-refactor: the twin exists to replace this
 * component, and importing one from the other would couple them for exactly as long as that migration
 * takes. Every other member is duplicated between them for the same reason.
 */
export type OfferActionTab = 'accept' | 'counter' | 'decline';

@Component({
    selector: 'app-counter-offer',
    templateUrl: './counter-offer.component.html',
    styleUrls: ['./counter-offer.component.scss'],
    imports: [
        CommonModule,
        TranslocoDirective,
        CharacterCountComponent,
        DatalistItemComponent,
        TooltipComponent,
        CurrencyInputComponent,
        ActionSubmittingComponent,
        ActionSubmittedComponent,
        OfferModalsHeaderComponent,
        VehicleChangedWarningComponent,
        OfferHistoryComponent,
        RouterLink,
        ReactiveFormsModule,
        CharacterCountDirective,
        ModalCloseComponent,
        ModalPriceBreakdownComponent
    ]
})
export class CounterOfferComponent extends OfferModalAction implements OnInit {
  form: UntypedFormGroup;

  commentLength = 0;
  declinedOffer = false;
  acceptedOffer = false;
  counteredOffer = false;
  BuyErrors = BuyErrors;
  counterOfferError: string = null;
  sellingDisclosureUrl: string;
  private readonly destroyed$ = new Subject();
  characterCountData: any;
  toolTipContent: any;
  textCounterOffer = {
    // Both of these are flag-off only since M4.10: the all-in layout renders neither the Selling
    // Disclosures paragraph this closes nor the legacy transportation sentence.
    textBindingAgreement: '',
    textTransportationDescription: '',
    // Resolved here as well as in the template because the all-in breakdown takes its base-amount
    // label as an input on a translations object, not as an interpolation.
    labelSeller: ''
  };

  inputConfig: InputConfig = {
    inputClass: 'form-control modal__form-control',
    inputName: 'counterOfferAmount',
    inputFormControlName: 'counterOfferAmount',
    inputAttrAriaLabelledBy: 'counter-offer-amount-label counter-offer-amount-error',
    thousandSeparator: '',
    isFrenchCanadian: this.isFrenchCanadian
  };

  get errorLocation(): 'modal' | 'counter' {
    if (!this.errorMessage) {
      return null;
    }

    if (
      this.errorMessage === BuyErrors.MSG_BUY_002 ||
      this.errorMessage === BuyErrors.MSG_BUY_003 ||
      this.errorMessage === BuyErrors.MSG_BUY_004 ||
      this.errorMessage === BuyErrors.MSG_BUY_005 ||
      this.errorMessage === BuyErrors.MSG_BUY_007 ||
      this.errorMessage === BuyErrors.MSG_BUY_014
      // MSG_BUY_014 is a function
    ) {
      return 'counter';
    }
    return 'modal';
  }

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _analyticsService: AnalyticsService,
    private readonly _route: ActivatedRoute,
    private readonly _store: Store,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly _translocoService: TranslocoService,
    private readonly _tooltipService: FeeToolTipService,
    private readonly translocoService: TranslocoService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly dateUtilService: DateUtilsService,
    private readonly dripPricingService: DripPricingService
  ) {
    super();
  }

  get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isFrenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  ngOnInit() {
    this.form = this._formBuilder.group({
      counterOfferAmount: [''],
      offerComment: ['']
    });
    this._store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(configuration => {
        this.sellingDisclosureUrl = get(configuration, 'sellingDisclosureUrl', null);
      });
    this.getTranslations();
  }

  getTranslations() {
    const keyParams = {
      'common.currency': {},
      UIComponents: {},
      'vehicles.counterOffer': {}
    };
    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translatedObj => {
        this.inputConfig.thousandSeparator = translatedObj[0].thousandSeparator;
        this.characterCountData = translatedObj[1].characterCount;
        this.textCounterOffer.textBindingAgreement = translatedObj[2].textBindingAgreement;
        this.textCounterOffer.textTransportationDescription = translatedObj[2].textTransportationDescription;
        this.textCounterOffer.labelSeller = translatedObj[2].labelSeller;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get showSellingDisclosure(): boolean {
    return this._launchDarklyService.getFeatureFlag('disclosuresLinkEnabled', false);
  }

  get offerComment() {
    return this.form && this.form.get('offerComment');
  }

  get vehicleTitle() {
    const { modelYear = '', make = '', model = '', trim = '', vin = '' } = this.item;
    return `${modelYear} ${make} ${model} ${trim} VIN ${vin}`;
  }

  replaceInvalidCharacters(e) {
    e.target.value = multiLanguageAlphaNumericRegexHelper(e.target.value);
  }

  formatCurrency(currency, withCents?) {
    return this.currencyFormatService.formatCurrency(currency, withCents);
  }

  counter() {
    this.declinedOffer = false;
    this.acceptedOffer = false;
    this.counteredOffer = true;

    this.errorMessage = null;
    const offerAmount = this.parsedCounterAmount();
    const offerComment = this.form.get('offerComment').value;

    if (offerAmount) {
      this.event.emit(new CounterOffer(offerAmount, offerComment, this.offerHistory[0].offer.offerUuid));
    } else {
      this.errorMessage = BuyErrors.MSG_BUY_003;
      const error = document.getElementById('counter-offer-amount');
      error.focus();

      // send tracking data
      const inventoryItemUuid = get(this._route, 'snapshot.parent.children[0].params.inventoryItemUuid', null);
      const analyticsPayload = new CounterOffer(offerAmount, offerComment, inventoryItemUuid);

      this._analyticsService.track('customEvent', {
        name: analyticsPayload.type.split('Offer').join('') + ' Offer',
        attr: this._analyticsService.createOfferModalEventAnalyticsPayload(
          analyticsPayload,
          this.errorMessage,
          inventoryItemUuid
        )
      });
    }

    this.form.reset();
  }

  analyticsInfo(pageDetails: string) {
    this._analyticsService.trackEvent('app-tooltip', pageDetails);
  }

  accept() {
    const offerComment = this.form.get('offerComment').value;
    this.acceptedOffer = true;
    this.declinedOffer = false;
    this.counteredOffer = false;

    this.counterOfferError = null;
    this.event.emit(new AcceptOffer(this.offerHistory[0].offer.offerUuid, 'ACCEPT', offerComment));
  }

  decline() {
    const offerComment = this.form.get('offerComment').value;
    this.counterOfferError = null;
    this.event.emit(new DeclineOffer(this.offerHistory[0].offer.offerUuid, 'DECLINE', offerComment));
    this.declinedOffer = true;
    this.acceptedOffer = false;
    this.counteredOffer = false;
  }

  cancel() {
    this.event.emit(new Cancel());
  }

  /**
   * We assume that we only care about the first item in the offer history, since
   * each user should only have one active offer on a vehicle at a time.
   */
  get offerChain() {
    return this.offerHistory && this.offerHistory.length ? this.offerHistory[0] : undefined;
  }

  get rootOffer() {
    return this.offerChain ? this.offerChain.offer : undefined;
  }

  get bidItemBuyFeeTotal(): number {
    return this.bidItem?.buyFee?.total ? this.bidItem.buyFee.total : 0;
  }

  /**
   * The counter amount as digits.
   *
   * counter() and the counter breakdown's base amount both read this, so the number submitted and the
   * number totalled are the same number. The masked field already hands back separator-free digits
   * (ngx-mask defaults dropSpecialCharacters to true), so the strip is not what makes today's
   * behaviour correct -- it is what keeps it correct if that ever changes. parseInt('59,000') is 59,
   * which under a binding-counter disclosure is a wrong figure rather than an obvious failure.
   *
   * String() rather than a null check: form.reset() leaves the control null, which yields NaN exactly
   * as the previous inline parseInt(null) did. counter()'s falsy guard rejects it and the breakdown
   * renders it as '$--'.
   */
  private parsedCounterAmount(): number {
    return parseInt(String(this.form?.get('counterOfferAmount')?.value).replace(/[^\d]/g, ''), 10);
  }

  // ---------------------------------------------------------------------------
  // All-in pricing (drip pricing, Canada's Competition Act) -- M4.5
  //
  // This is the only M4 modal that quotes TWO amounts the buyer can be bound to, so it renders two
  // breakdowns rather than one:
  //
  // - The seller's offer, which the system supplies. It replaces the SELLER OFFER datalist item and --
  //   because the breakdown itemises exactly the fees the FEES row and its tooltip carry -- that row and
  //   its (i) too. Unlike M3's list-surface row, the FEES row here carries neither a tax note nor a
  //   transportation link, so nothing is lost by dropping it.
  // - The buyer's counter, which the buyer types. The currency field is projected into the breakdown's
  //   base-amount row and the total beneath it updates as they type.
  //
  // BOTH are Pattern B (ADDITIVE). The seller breakdown was Pattern A (DECOMPOSED) as shipped in M4.5;
  // M4.9 changed it to match the Accept comp (Figma 10503:24898), which labels the RAW $53,000 'SELLER
  // OFFER' and adds the fees beneath it. The total is identical either way -- this is presentation, not
  // arithmetic -- but Pattern A labelled the all-in figure 'SELLER OFFER' and relabelled the seller's
  // actual number 'VEHICLE PRICE', i.e. it attributed to the seller a number the seller never named.
  // The additive reading also matches M4.8's, so the two View Offer states now agree.
  //
  // Transportation is quoted in BOTH totals, because it applies to either outcome. That it therefore
  // appears twice on screen is a layout consequence of two breakdowns in one view, not a duplicated
  // charge: each total is what that particular outcome would cost.
  //
  // textSelectCounter's "you agree to purchase the vehicle at this price" is deliberately NOT
  // reworded, as in M4.3 and M4.4 and unlike M4.2: for a counter the fees genuinely are added on top
  // of the amount the buyer names. textBindingAgreement IS swapped, because it names the fees
  // explicitly as being outside the price and that is exactly what stops being true.
  // ---------------------------------------------------------------------------

  /** Prefix for the seller breakdown's generated ids, matching this modal's vom_* hooks. */
  readonly dripIdPrefix = 'vom_price';

  /** Prefix for the counter breakdown's ids. Distinct, or the two breakdowns collide on every id. */
  readonly dripCounterIdPrefix = 'vom_counter_price';

  /**
   * Pattern B for both breakdowns: a bare base row with the fees added beneath it.
   *
   * The seller's offer is the base of one and the buyer's typed counter is the base of the other; the
   * difference between them is which amount is bound and whether the row is a figure or a field, not
   * how the fees relate to it.
   */
  readonly additivePattern = ModalPriceBreakdownPattern.ADDITIVE;

  /**
   * The listing's fee schedule, forwarded by OfferModalsComponent.
   *
   * buyFee rather than bidItem.buyFee: this modal opens for vehicles that are not on the buyer's bid
   * list, where bidItem is undefined. bidItemBuyFeeTotal and feeDetailsTooltip deliberately keep
   * reading bidItem, so the flag-off path behaves exactly as it does today.
   */
  private get dripFeeSource(): DripFeeSource {
    return this.buyFee;
  }

  /** The raw seller offer: the breakdown's base row, with the fees added beneath it. */
  get dripSellerBaseAmount(): number | undefined {
    return this.rootOffer?.amount;
  }

  /**
   * The seller's offer plus the required fees.
   *
   * Read by showDripBreakdown as a validity check, and only for that since M4.10 -- the Accept tab's
   * binding sentence now quotes dripSellerTotalDue. Kept as the gate because "are there fees to add
   * yet?" is the question showDripBreakdown is asking, and that is independent of transportation. It is
   * deliberately NOT bound to the breakdown's baseAmount: the component adds the fee rows itself to
   * reach the total, so passing an already-all-in figure would double-count every one of them and
   * produce a plausible-looking wrong number under a compliance label rather than a crash.
   */
  get dripSellerAllInAmount(): number | undefined {
    return this.dripPricingService.allInPriceOf(this.dripSellerBaseAmount, this.dripFeeSource);
  }

  /**
   * One gate for BOTH breakdowns, so this modal is never half-migrated -- an all-in counter beside a
   * bare seller offer invites exactly the comparison the epic exists to prevent.
   *
   * Three conditions, each load-bearing:
   *
   * - the flag, so the kill switch works here as everywhere else;
   * - a fee payload, because it IS the fee source. Without one requiredFeesOf returns [] and each
   *   "total" would equal its bare amount, presenting an unadjusted figure as an all-in one;
   * - a finite seller all-in amount, which covers an offer chain that has not arrived yet.
   *
   * Tested with hasFeePayload rather than `!!this.buyFee`. Truthiness is not the same question: `{}`
   * and `{ fees: null }` are both truthy and both carry no fees, so `!!` admits exactly the case the
   * bullet above says it exists to exclude. hasFeePayload asks whether a fee COLLECTION arrived, which
   * is the condition that makes the totals meaningful.
   *
   * Not requiredFeesOf(...).length either, and the service docblock says so explicitly: a payload that
   * arrived carrying no required fee must still render, with each all-in total legitimately equal to
   * its bare amount. Gating on the row count would withhold both breakdowns from a correct zero-fee
   * answer.
   *
   * Deliberately NOT gated on the counter field having a value: the buyer types it, so an empty field
   * is the normal opening state and the breakdown has to be on screen to be typed into.
   */
  get showDripBreakdown(): boolean {
    return this.dripPricingService.isDripPricingEnabled
      && this.dripPricingService.hasFeePayload(this.dripFeeSource)
      && this.dripSellerAllInAmount !== undefined;
  }

  /**
   * The counter breakdown's base amount: what the buyer has typed, or undefined when the field is
   * empty.
   *
   * undefined rather than 0, so an empty field renders the '$--' placeholder. A total of $0 under a
   * binding-counter disclosure is a materially wrong figure; a missing one is merely missing.
   */
  get dripCounterBaseAmount(): number | undefined {
    const parsed = this.parsedCounterAmount();
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  get dripTransportationFee(): number | undefined {
    return this.dripPricingService.transportationFeeOf(this.dripFeeSource);
  }

  /**
   * Governs the in-panel "Transportation is optional" sentence (M4.10). Same exported helper the rows
   * themselves use, so the two cannot drift -- do not re-derive it as `fee > 0`.
   *
   * There is no longer a showTransportationParagraph beside this. It existed to keep the paragraph
   * unconditional while the flag was off, but the flag-off paragraph now lives inside an
   * `@if (!showDripBreakdown)` block where that getter could only ever return true.
   */
  get showTransportationDisclosure(): boolean {
    return showsTransportationFee(this.dripTransportationFee);
  }

  /**
   * The fee rows, cached by content, and shared by both breakdowns -- one fee schedule, so the seller
   * total and the counter total cannot itemise different fees.
   *
   * The cache is not an optimisation: translatedRequiredFeesOf maps a fresh array per call and a
   * template binding is re-evaluated every change-detection pass, so returning it directly would hand
   * the OnPush children a new identity each pass and trip NG0100 in development builds. Keying on the
   * serialised content keeps the reference stable while still picking up a real change.
   */
  get dripFeeRows(): ModalPriceBreakdownRow[] {
    const rows = this._tooltipService.translatedRequiredFeesOf(this.dripFeeSource);
    const key = JSON.stringify(rows);
    if (key !== this.dripFeeRowsKey) {
      this.dripFeeRowsKey = key;
      this.dripFeeRowsCache = rows;
    }
    return this.dripFeeRowsCache;
  }

  /**
   * The seller breakdown's strings, cached by content for the same reason as dripFeeRows.
   *
   * labelBaseAmount is vehicles.counterOffer.labelSeller rather than a new drip key: it is already
   * 'SELLER OFFER' / 'OFFRE VENDEUR' in caps, it is the words this modal uses for this figure today,
   * and a second key saying the same thing is a drift risk with no upside. Since M4.9 it labels the
   * seller's actual number rather than the all-in figure, which is what the seller actually offered.
   */
  get dripSellerBreakdownTranslations(): ModalPriceBreakdownTranslations {
    const translations = this.dripBreakdownTranslationsFor(this.textCounterOffer.labelSeller);
    const key = JSON.stringify(translations);
    if (key !== this.dripSellerTranslationsKey) {
      this.dripSellerTranslationsKey = key;
      this.dripSellerTranslationsCache = translations;
    }
    return this.dripSellerTranslationsCache;
  }

  /**
   * Pattern B's strings, cached by content.
   *
   * labelBaseAmount is the drip bundle's ALL-CAPS labelCounterOffer rather than
   * vehicles.counterOffer.btnCounter: that key is 'Counter Offer' and is uppercased by the replaced
   * label's text-uppercase class, which the breakdown's own label element does not carry.
   */
  get dripCounterBreakdownTranslations(): ModalPriceBreakdownTranslations {
    const translations = this.dripBreakdownTranslationsFor(
      this._tooltipService.dripTranslations.labelCounterOffer
    );
    const key = JSON.stringify(translations);
    if (key !== this.dripCounterTranslationsKey) {
      this.dripCounterTranslationsKey = key;
      this.dripCounterTranslationsCache = translations;
    }
    return this.dripCounterTranslationsCache;
  }

  /**
   * Everything the two translation objects share. Only labelBaseAmount differs between them, and
   * spelling the other five out twice is how the seller total and the counter total would end up
   * labelled differently.
   */
  private dripBreakdownTranslationsFor(labelBaseAmount: string): ModalPriceBreakdownTranslations {
    const drip = this._tooltipService.dripTranslations;
    return {
      labelBaseAmount,
      labelVehiclePrice: drip.labelVehiclePrice,
      labelTransportationFee: this._tooltipService.feeTranslations.TRANSPORTATION_FEE,
      // labelModalTotalDue, never labelTotalPurchasePrice: these totals INCLUDE transportation while
      // the list-surface total excludes it. See the collision note on FeeToolTipService.dripTranslations.
      labelTotalDue: drip.labelModalTotalDue,
      textTotalPlaceholder: drip.textTotalPlaceholder,
      ariaLabelPriceComponents: drip.ariaLabelPriceComponents
    };
  }

  /**
   * inputConfig with the labelling element the current layout actually renders.
   *
   * The field is projected out from under its own <label> and into the breakdown, which names it with
   * its own base-amount label instead, so the labelledby has to follow it. It is on the config object
   * rather than a template binding, so it cannot be passed as template context.
   *
   * Mutates and returns the SAME object rather than spreading a copy: a getter handing a template a
   * fresh object every change-detection pass trips NG0100 in development builds. One config also
   * means getTranslations()' thousandSeparator patch keeps landing on the object the template reads.
   *
   * Which makes this depend on CurrencyInputComponent NOT being OnPush, invisibly from here. RUIC
   * declares it with no changeDetection, so it is Default and re-evaluates
   * [attr.aria-labelledBy]="inputConfig.inputAttrAriaLabelledBy" on every pass -- the only reason
   * mutating a field on an unchanged reference reaches the DOM at all. If it were made OnPush nothing
   * here would fail loudly: the breakdown would still render, the field would still work, and the input
   * would keep pointing at whichever label existed when the config was first read, so a screen reader
   * would announce the wrong label for a money field and nothing would say so. The fix would not be
   * local either -- a fresh reference per change is the NG0100 problem above, so it needs a signal input
   * or an ngOnChanges on RUIC's side.
   */
  get counterAmountInputConfig(): InputConfig {
    const labelledBy = this.showDripBreakdown
      ? `${this.dripCounterIdPrefix}_base_label counter-offer-amount-error`
      : 'counter-offer-amount-label counter-offer-amount-error';
    if (this.inputConfig.inputAttrAriaLabelledBy !== labelledBy) {
      this.inputConfig.inputAttrAriaLabelledBy = labelledBy;
    }
    return this.inputConfig;
  }

  // ---------------------------------------------------------------------------
  // View Offer action tabs -- M4.9
  //
  // The comps (Figma 10503:24898 Accept, 10503:24685 Counter) replace this modal's two-column layout --
  // Accept and Decline in one column, Counter in the other, with both breakdowns and both sets of prose
  // on screen at once -- with three tabs, each showing only its own breakdown, its own binding sentence
  // and its own button. That is the fix for the reported defect: the buyer was being shown two totals
  // for two different outcomes simultaneously, which is the comparison the epic exists to prevent.
  //
  // Gated entirely on showDripBreakdown. Flag off, the two-column layout renders exactly as today --
  // this is a tenant-visible layout change, and it ships with the pricing it exists to present.
  //
  // Declarative, NOT the imperative pattern in book-value-modal (which walks parentNode, rewrites
  // classes by hand and indexes attributes positionally: `attributes[11].value = 0`). The ARIA and
  // analytics attribute contract is the same as that component's -- role=tablist/tab/tabpanel,
  // aria-selected, aria-controls, aria-labelledby, roving tabindex, arrow-key navigation,
  // data-track-elem="tab" + data-track-trigger="switch" -- so the behaviour a screen reader and the
  // analytics pipeline see is unchanged. Only the implementation is bindings instead of DOM surgery.
  //
  // NO COMP EXISTS FOR THE DECLINE TAB. It renders the seller offer, the shared comments field and the
  // Decline button, and deliberately no breakdown and no binding sentence: declining creates no payment
  // obligation, so there is no total to disclose. Raised with the story owner rather than invented.
  // ---------------------------------------------------------------------------

  /**
   * Accept first, matching the comps' tab order.
   *
   * A tablist with no selected tab is not a state the pattern allows, so something has to be active on
   * open; first-tab-active is the convention and the comps show both Accept and Counter as the selected
   * tab in their respective frames, i.e. neither is "the" default. Selecting a tab only reveals a panel
   * -- no action is submitted until its button is pressed -- so this does not pre-arm the binding path.
   */
  activeTab: OfferActionTab = 'accept';

  /** Tab order, used for both rendering and arrow-key navigation so the two cannot disagree. */
  readonly offerActionTabs: readonly OfferActionTab[] = ['accept', 'counter', 'decline'];

  @ViewChildren('offerActionTab') offerActionTabRefs: QueryList<ElementRef<HTMLButtonElement>>;

  isActiveTab(tab: OfferActionTab): boolean {
    return this.activeTab === tab;
  }

  /**
   * Switching tabs clears the error, and with it the title line above the form: the error belongs to the
   * panel the buyer has just left.
   */
  selectTab(tab: OfferActionTab): void {
    if (tab !== this.activeTab) {
      this.errorMessage = null;
    }
    this.activeTab = tab;
  }

  /**
   * Roving tabindex: only the selected tab is reachable with Tab, and the arrows move between them.
   *
   * Activation follows focus, as in the rest of the app's tabs -- the panels are cheap and there is no
   * work to defer. Home/End are included because the pattern expects them and three tabs make them the
   * quickest way to reach Decline.
   *
   * Focus is moved imperatively because selecting a tab re-renders which button carries tabindex="0";
   * the browser does not follow that on its own. Guarded on the ref being present so a keystroke during
   * teardown cannot throw.
   */
  onTabKeydown(event: KeyboardEvent): void {
    const current = this.offerActionTabs.indexOf(this.activeTab);
    let next = current;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % this.offerActionTabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + this.offerActionTabs.length) % this.offerActionTabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.offerActionTabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(this.offerActionTabs[next]);
    this.offerActionTabRefs?.get(next)?.nativeElement?.focus();
  }

  /**
   * The Accept tab's binding sentence quotes the MODAL TOTAL -- base + required fees + transportation
   * -- and not the all-in figure that leaves transportation out (M4.10, per Figma 10503:24898).
   *
   * This REVERSES the M4.2 precedent, here only, and the reversal is the considered position rather
   * than a concession to the comp.
   *
   * M4.2's argument was that transportation is separately elected and avoidable by collecting the
   * vehicle, so a transportation-inclusive figure under "you agree to purchase" overstates the
   * obligation. The comp answers that in the very next paragraph instead of by lowering the number: it
   * quotes the inclusive total and then tells the buyer, directly beneath, that transportation is
   * optional and that Cancel takes them back to remove it. With that sentence on screen the inclusive
   * figure is accurate and the escape route is stated, which is strictly more information than a
   * quietly smaller number.
   *
   * What settles it is the failure mode of the alternative. This sentence sits immediately under a bold
   * TOTAL PURCHASE PRICE row, so quoting a figure SMALLER than the total directly above reads as a
   * discrepancy rather than as a narrower obligation -- and it is the kind of discrepancy a buyer
   * resolves in their own favour. Two numbers in one panel, one of them unexplained, is worse than
   * either number chosen consistently.
   *
   * Buy Now (M4.2) still quotes all-in and is raised with the story owner rather than changed here;
   * its panel has no transportation row and no total above the sentence, so neither argument applies.
   *
   * Whole dollars, matching the comp and the TOTAL PURCHASE PRICE row this sits under. withCents:false
   * is a literal .replace('.00','') inside formatCurrency rather than rounding, so a genuinely
   * fractional total still shows its cents instead of being quietly misquoted. It must move in step
   * with the breakdown's own [showCents]="false" -- the sentence and the bold row above it quote the
   * same obligation, so one of them carrying cents is a disagreement a buyer reads in their favour.
   */
  get acceptQuotedPriceDisplay(): string {
    return this.formatCurrency(this.dripSellerTotalDue, false);
  }

  /**
   * The seller's offer as a modal total: the required fees and transportation on top of the number the
   * seller actually named. The same function the breakdown's own total row uses, so the bold figure and
   * the sentence beneath it cannot disagree -- that is the whole point of quoting this one.
   */
  get dripSellerTotalDue(): number | undefined {
    return modalTotalDueOf(this.dripSellerBaseAmount, this.dripFeeRows, this.dripTransportationFee);
  }

  /** The counter's equivalent: what the buyer would owe if the seller accepts their number. */
  get dripCounterTotalDue(): number | undefined {
    return modalTotalDueOf(this.dripCounterBaseAmount, this.dripFeeRows, this.dripTransportationFee);
  }

  /**
   * The Counter tab's binding sentence.
   *
   * Falls back to the breakdown's own '$--' placeholder rather than formatting undefined: the buyer has
   * not typed an amount yet on open, and '$0' or '$NaN' inside a binding-counter sentence is a wrong
   * figure rather than an obviously missing one. The comp writes this state as '$X listed'.
   *
   * Whole dollars once there IS an amount, for the same reason as the Accept tab: this sentence sits
   * under the counter breakdown's own TOTAL row, which now renders without cents too. The placeholder
   * branch is unaffected -- it is a literal, not a formatted figure.
   */
  get counterQuotedPriceDisplay(): string {
    const amount = this.dripCounterTotalDue;
    return amount === undefined
      ? this._tooltipService.dripTranslations.textTotalPlaceholder
      : this.formatCurrency(amount, false);
  }

  private dripFeeRowsKey: string;
  private dripFeeRowsCache: ModalPriceBreakdownRow[];
  private dripSellerTranslationsKey: string;
  private dripSellerTranslationsCache: ModalPriceBreakdownTranslations;
  private dripCounterTranslationsKey: string;
  private dripCounterTranslationsCache: ModalPriceBreakdownTranslations;

  get feeDetailsTooltip() {
    this.toolTipContent = this._tooltipService.getFeeValues(this.bidItem.buyFee.fees);
    return { body: this.toolTipContent };
  }

  get hasBuyFees(): boolean {
    return this.bidItemBuyFeeTotal > 0;
  }

  get isMobile(): boolean {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  showBuyFeesInOffers(): boolean {
    return this._launchDarklyService.getFeatureFlag('offerModal-feesEnabled', false);
  }

  isSubmittingOffer(): boolean {
    return (
      this.waitingResponse &&
      !this.successfulResponse &&
      !this.acceptedOffer &&
      !this.declinedOffer &&
      this.counteredOffer
    );
  }

  isSubmittingDecline(): boolean {
    return this.waitingResponse && !this.successfulResponse && !this.acceptedOffer && this.declinedOffer;
  }

  isSubmittingAccept(): boolean {
    return this.waitingResponse && !this.successfulResponse && this.acceptedOffer && !this.declinedOffer;
  }

  isCounterAccepted(): boolean {
    return (
      !this.waitingResponse &&
      this.successfulResponse &&
      !this.acceptedOffer &&
      !this.declinedOffer &&
      this.counteredOffer
    );
  }

  isOfferDeclined(): boolean {
    return !this.waitingResponse && this.successfulResponse && !this.acceptedOffer && this.declinedOffer;
  }

  isOfferAccepted(): boolean {
    return !this.waitingResponse && this.successfulResponse && this.acceptedOffer && !this.declinedOffer;
  }
}
